import { Injectable, EventEmitter } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, Params, PRIMARY_OUTLET } from '@angular/router';

import { filter } from 'rxjs/operators';

@Injectable()
export class BreadcrumbService {
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router
  ) {
    this._initialize();
  }

  public get breadcrumbs(): Breadcrumb[] {
    return this._breadcrumbs;
  }
  public set breadcrumbs(breadcrumbs: Breadcrumb[]) {
    this._breadcrumbs = breadcrumbs;
    this.onChange.emit(this.breadcrumbs);
  }

  public get visible(): boolean {
    return this._is_visible;
  }

  public onChange: EventEmitter<Breadcrumb[]> = new EventEmitter();

  private _ROUTE_DATA_TITLE_BREADCRUMB = '$title';
  private _ROUTE_DATA_IGNORE_BREADCRUMB = '$ignore';

  private _is_visible = false;
  private _breadcrumbs: Breadcrumb[] = [];
  private _url = '';

  public show() {
    this._is_visible = true;
  }

  public hide() {
    this._is_visible = false;
  }

  public update() {
    this._url = '';
    this._breadcrumbs = [];

    this._setBreadcrumbs(this._activatedRoute.root);
    this.onChange.emit(this.breadcrumbs);

    this._is_visible = this.breadcrumbs.length > 1 ? true : false;
  }

  private _initialize() {

    // subscribe to the NavigationEnd event
    this._router.events
      .pipe(filter((event) => event.constructor == NavigationEnd))
      .subscribe((event) => this.update());

    this.update();
  }

  private _setBreadcrumbs(route: ActivatedRoute) {

    // get the child routes
    const children: Array<ActivatedRoute> = route.children;

    // return if there are no more children
    if (children.length === 0) {
      if (this._breadcrumbs.length === 0 && !route.snapshot.data[this._ROUTE_DATA_IGNORE_BREADCRUMB]) {
        const breadcrumb = this._buildCrumb(route);
        if (breadcrumb) { this._breadcrumbs.push(breadcrumb); }
      }
      this._url = '';
      // END
    }

    // iterate over each children
    children.forEach((child) => {

      // verify primary route
      if (child.outlet !== PRIMARY_OUTLET) { return; }

      // verify the custom data property "breadcrumb" is specified on the route
      if (child.snapshot.url.length == 0) {
        this._setBreadcrumbs(child);
        return;
      }

      const breadcrumb = this._buildCrumb(child);
      if (breadcrumb) { this._breadcrumbs.push(breadcrumb); }

      // recursive
      this._setBreadcrumbs(child);
    });
  }

  private _buildCrumb(route: ActivatedRoute): Breadcrumb {

    const _route = route.snapshot;

    if (!_route) { return null; }

    // get and append the route's URL segment to URL
    const routeURL: string = _route.url.map(segment => segment.path).join('/');
    this._url += `/${routeURL}`;

    if (_route.data[this._ROUTE_DATA_IGNORE_BREADCRUMB]) { return null; }

    if (!_route.data[this._ROUTE_DATA_TITLE_BREADCRUMB]) {
      let title: string;

      if (_route.routeConfig && _route.routeConfig.children) {
        _route.routeConfig.children.forEach((child) => {
          if (
            !title &&
            (child.path == '' || child.path == '/') &&
            child.data &&
            child.data[this._ROUTE_DATA_TITLE_BREADCRUMB] &&
            !child.data[this._ROUTE_DATA_IGNORE_BREADCRUMB]
          ) {
            title = child.data[this._ROUTE_DATA_TITLE_BREADCRUMB];
          }
        });
      }

      if (title) { _route.data[this._ROUTE_DATA_TITLE_BREADCRUMB] = title; }

      if (!_route.data[this._ROUTE_DATA_TITLE_BREADCRUMB]) { return null; }
    }

    // add breadcrumb
    return {
      label: _route.data[this._ROUTE_DATA_TITLE_BREADCRUMB],
      params: _route.params,
      url: this._url
    };
  }
}

export interface Breadcrumb {
  label: string;
  params?: Params;
  url: string;
}
